import React, { useState, FunctionComponent } from "react";
import Link from "next/link";
import { MutationHookOptions, useMutation } from "@apollo/client";
import { useRouter } from "next/router";

import {
  removeUserFromOrgMutation,
  updateUserOrgRoleMutation,
  updateMyJobTitleMutation,
  removeMeFromOrgMutation,
} from "src/lib/graphql/org.queries";

import { meQuery } from "src/lib/graphql/user.queries";

import { User, Org } from "src/lib/graphql/types";
import { toJpeg } from "src/lib/imageCompressor";

import LoadingIndicator from "src/components/general/LoadingIndicator";
import Dropdown from "src/components/Dropdown";

type OrgUserRowProps = {
  org: Org;
  orgUser: User;
  user: User;
  orgAdmin: boolean;
  permissionOptions: { value: string; label: string }[];
  adminFlag?: boolean;
};

const OrgUserRow: FunctionComponent<OrgUserRowProps> = ({
  org,
  orgUser,
  user,
  orgAdmin,
  permissionOptions,
  adminFlag,
}) => {
  const router = useRouter();

  const [removeUser, { loading: removeUserLoading }] = useMutation(
    removeUserFromOrgMutation
  );

  const [updateUser, { loading: updateUserLoading }] = useMutation(
    updateUserOrgRoleMutation
  );

  const removeMeOptions: MutationHookOptions = {
    refetchQueries: [{ query: meQuery }],
    awaitRefetchQueries: true,
    onCompleted: () => {
      const currentPath = router.asPath;

      if (currentPath.startsWith("/account")) {
        router.push("/account/orgs");
      } else {
        // this implicitly assumes an org profile page
        router.push(`/@${router.query.shortname}`);
      }
    },
  };

  const updateMyTitleOptions: MutationHookOptions = {
    refetchQueries: [{ query: meQuery }],
    awaitRefetchQueries: true,
  };

  const [updateMyTitle, { loading: updateMyTitleLoading }] = useMutation(
    updateMyJobTitleMutation,
    updateMyTitleOptions
  );

  const [removeMe, { loading: removeMeLoading }] = useMutation(
    removeMeFromOrgMutation,
    removeMeOptions
  );

  const loading = [
    removeUserLoading,
    updateUserLoading,
    updateMyTitleLoading,
    removeMeLoading,
  ];

  const userOrgInfo = orgUser.memberOf!.find(
    (info) => info?.org?.id === org.id
  );

  const [newTitle, setNewTitle] = useState(userOrgInfo!.jobTitle || "");

  const selectedRole = permissionOptions.find(
    (p) => p.value === userOrgInfo!.role?.id
  );

  return (
    <tr>
      <td className="user-avatar">
        <LoadingIndicator loading={!!loading.find((x) => x)} />
        {orgUser.image && (
          <img
            src={toJpeg(orgUser.image, 100)}
            alt={`${orgUser.fullName}'s avatar`}
          />
        )}
      </td>
      <td className="user-name">
        <Link href={`/@${orgUser.shortname}`} prefetch={false}>
          <a>{!adminFlag ? orgUser.fullName : orgUser.email}</a>
        </Link>
      </td>
      <td className="user-title">
        {orgAdmin ? (
          <input
            type="text"
            className="input"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onBlur={() =>
              updateUser({
                variables: {
                  orgId: org.id,
                  jobTitle: newTitle,
                  userId: orgUser.id,
                },
              })
            }
          />
        ) : orgUser.id === user?.id ? (
          <input
            type="text"
            className="input"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onBlur={() =>
              updateMyTitle({
                variables: {
                  orgId: org.id,
                  jobTitle: newTitle,
                },
              })
            }
          />
        ) : (
          userOrgInfo!.jobTitle
        )}
      </td>
      <td className="org-user-permissions">
        {orgAdmin ? (
          <Dropdown
            options={permissionOptions}
            value={selectedRole}
            isSearchable={false}
            onChange={(role: any) => {
              if (role) {
                updateUser({
                  variables: {
                    orgId: org.id,
                    roleId: role.value,
                    userId: orgUser.id,
                  },
                });
              }
            }}
          />
        ) : (
          userOrgInfo!.role?.name
        )}
      </td>
      <td className="remove-user">
        {orgAdmin ? (
          <button
            onClick={() => {
              // if user tries to remove themselves
              if (orgUser.id === user?.id) {
                return removeMe({
                  variables: { orgId: org.id },
                });
              }

              removeUser({
                variables: { orgId: org.id, userId: orgUser.id },
              });
            }}
          >
            {orgUser.id === user?.id ? "Leave" : "Remove"}
          </button>
        ) : orgUser.id === user?.id ? (
          <button
            onClick={() =>
              removeMe({
                variables: { orgId: org.id },
              })
            }
          >
            Leave
          </button>
        ) : (
          ""
        )}
      </td>
    </tr>
  );
};

export default OrgUserRow;
